import { cloneDraft, matchesPurchaseOrder } from './shared/purchase-order-state';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RmsModule } from '@rms';
import { RmsMessageService, WebService, lastValueFrom } from '@services';
import html2pdf from 'html2pdf.js';

import { deepStrip } from '../../../../shared/utils/deep-strip';
import { createEmptyPurchaseOrder } from '../../../../shared/utils/factory/purchase-order.factory';
import { InventoryFormComponent } from './inventory-form/inventory-form.component';
import { MultiPurchaseOrderFormComponent } from './multi-purchase-order-form/multi-purchase-order-form.component';
import { PurchaseOrderFormComponent } from './purchase-order-form/purchase-order-form.component';
import { SpreadPurchaseOrderFormComponent } from './spread-purchase-order-form/spread-purchase-order-form.component';

@Component({
  selector: 'app-purchase-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    RmsModule,
    PurchaseOrderFormComponent,
    MultiPurchaseOrderFormComponent,
    SpreadPurchaseOrderFormComponent
  ],
  templateUrl: './purchase-orders.component.html',
  styleUrl: './purchase-orders.component.scss'
})
export class PurchaseOrdersComponent implements OnInit {
  @Input() PurchaseOrderID: number | null = null;
  @Input() NewPurchaseOrder = false;
  @Input() MultiPurchaseOrder = false;
  @Input() OfferSample: any = null;

  @ViewChild('pdfContainer') pdfContainerRef!: ElementRef;

  inventoryFormComponentType = InventoryFormComponent;

  PrintPOPDF = false;
  loading = false;
  loadError = '';
  orderType = '';

  PurchaseOrders: any[] = [];
  selectedPurchaseOrder: any = null;
  selectedPurchaseOrders: any[] | undefined;
  SelectedPurchaseOrderSpread: string | undefined;

  searchOption: 'Reference #' | 'PO #' | 'Supplier Reference #' = 'PO #';
  searchText = '';

  readonly searchOptions = [
    { value: 'PO #', label: 'PO #' },
    { value: 'Reference #', label: 'Reference #' },
    { value: 'Supplier Reference #', label: 'Supplier Reference #' }
  ];

  readonly POColumns: any[] = [
    { key: 'PurchaseOrderLabel', label: 'PO #', width: 20, align: 'left' },
    { key: 'PurchaseDate', label: 'Purchase Date', width: 30, type: 'date', SearchType: 'date' },
    { key: 'SupplierName', label: 'Supplier', width: 50 }
  ];

  constructor(
    private web: WebService,
    private message: RmsMessageService
  ) { }

  async ngOnInit(): Promise<void> {
    if (this.NewPurchaseOrder) {
      this.startNewPurchaseOrder();
      this.NewPurchaseOrder = false;
    }

    if (this.PurchaseOrderID) {
      this.loading = true;
      try {
        const rows = await lastValueFrom(this.web.getWebCall('inventory', 'coffee/purchase-order', {
          params:[{name:'PurchaseOrderID',value:this.PurchaseOrderID}]
        }));
        const po = rows?.[0];
        if (!po) throw new Error('Purchase order not found.');
        if (po.PurchaseOrderSpread) this.openSpread(po.PurchaseOrderSpread);
        else this.openSinglePurchaseOrder(po);
      } catch (error) { this.loadError='Unable to load purchase order. Please retry.'; }
      finally { this.loading=false; }
      return;
    }
    await this.loadPurchaseOrders();
  }

  get showPurchaseOrderList(): boolean {
    return !this.selectedPurchaseOrder
      && !this.MultiPurchaseOrder
      && !this.SelectedPurchaseOrderSpread
      && !this.selectedPurchaseOrders;
  }

  get filteredPurchaseOrders(): any[] {
    return this.PurchaseOrders.filter(po =>
      (!this.orderType || (this.orderType === 'spread' ? !!po.PurchaseOrderSpread : !po.PurchaseOrderSpread)) &&
      matchesPurchaseOrder(po, this.searchOption, this.searchText ?? ''));
  }

  retryLoad(): void { void this.loadPurchaseOrders(); }
  async refreshPurchaseOrderList(): Promise<void> {
    await this.loadPurchaseOrders(false);
  }

  async onSelectPurchaseOrder(event: any): Promise<void> {
    const purchaseOrder = event?.row ?? event;

    if (!purchaseOrder) {
      return;
    }

    if (purchaseOrder.PurchaseOrderSpread) {
      this.openSpread(purchaseOrder.PurchaseOrderSpread);
      return;
    }

    this.openSinglePurchaseOrder(purchaseOrder);
  }

  ViewPurchaseOrderFromSpread(purchaseOrder: any): void {
    this.resetView();
    this.selectedPurchaseOrder = cloneDraft(purchaseOrder);
  }

  startNewPurchaseOrder(): void {
    this.resetView();
    this.selectedPurchaseOrder = createEmptyPurchaseOrder();
  }

  startNewMultiPurchaseOrder(): void {
    this.resetView();
    this.MultiPurchaseOrder = true;
  }

  ReturnToList(): void {
    this.resetView();
    if (!this.PurchaseOrders.length) void this.loadPurchaseOrders();
  }

  onCancelMultiPurchaseOrderForm(): void {
    this.resetView();
  }

  CloseSpreadScreen(newSpread: boolean): void {
    this.resetView();

    if (!newSpread && !this.PurchaseOrders.length) void this.loadPurchaseOrders();
    if (newSpread) {
      this.MultiPurchaseOrder = true;
    }
  }

  async onSavePO({ PurchaseOrderID, update }: { PurchaseOrderID: any; update: boolean }): Promise<void> {
    await this.loadPurchaseOrders(false);

    const savedPO = this.findPurchaseOrderById(PurchaseOrderID);

    if (savedPO) {
      this.openSinglePurchaseOrder(savedPO);
    } else {
      this.PurchaseOrderID = Number(PurchaseOrderID) || null;
    }

    this.message.success(
      update
        ? `Successfully Updated PO#${PurchaseOrderID}`
        : `Successfully Created PO#${PurchaseOrderID}`
    );
  }

  async onUpdateSpread(PurchaseOrderIDs: string): Promise<void> {
    await this.loadPurchaseOrders(false);
    this.openSpread(PurchaseOrderIDs);
    this.message.success('Successfully Updated Spread!');
  }

  async onSavePOSpread(PurchaseOrderIDs: any): Promise<void> {
    await this.loadPurchaseOrders(false);
    this.openSpread(PurchaseOrderIDs);
    this.message.success(`Successfully Created Purchase Orders ${PurchaseOrderIDs}`);
  }

  onReviewSpread(purchaseOrders: any[]): void {
    this.resetView();
    this.selectedPurchaseOrders = purchaseOrders;
  }

  generatePdf(): void {
    this.PrintPOPDF = true;

    setTimeout(() => {
      if (!this.pdfContainerRef?.nativeElement) {
        this.PrintPOPDF = false;
        return;
      }

      const options = {
        filename: 'InventoryForm.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
      };

      html2pdf()
        .set(options)
        .from(this.pdfContainerRef.nativeElement)
        .save()
        .then(() => {
          this.PrintPOPDF = false;
        })
        .catch((error: any) => {
          console.error('Failed to generate PO worksheet PDF:', error);
          this.PrintPOPDF = false;
          this.message.error('Unable to generate the PO worksheet.');
        });
    }, 300);
  }

  private async loadPurchaseOrders(handleRequestedPO = true): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      const allPOs: any[] = await lastValueFrom(
        this.web.getWebCall('inventory', 'coffee/purchase-order')
      );

      const standalonePOs: any[] = [];
      const groupedPOs: { [spreadKey: string]: any[] } = {};

      for (const po of allPOs ?? []) {
        if (!po.PurchaseOrderSpread) {
          standalonePOs.push(po);
          continue;
        }

        groupedPOs[po.PurchaseOrderSpread] ??= [];
        groupedPOs[po.PurchaseOrderSpread].push(po);
      }

      const summarizedPOs = Object.entries(groupedPOs).map(([spreadKey, purchaseOrders]) => {
        const sortedPOs = [...purchaseOrders].sort(
          (a: any, b: any) => Number(a.PurchaseOrderID) - Number(b.PurchaseOrderID)
        );

        const firstPO = sortedPOs[0];
        const lastPO = sortedPOs[sortedPOs.length - 1];

        return {
          ...firstPO,
          PurchaseOrderID: firstPO.PurchaseOrderID,
          PurchaseOrderLabel: `${firstPO.PurchaseOrderID} - ${lastPO.PurchaseOrderID}`,
          PurchaseOrderSpread: spreadKey,
          DisplayPO: 'SPREAD',
          TextColor: 'blue',
          GroupedPurchaseOrders: sortedPOs
        };
      });

      this.PurchaseOrders = [...standalonePOs, ...summarizedPOs].sort(
        (a: any, b: any) => Number(b.PurchaseOrderID) - Number(a.PurchaseOrderID)
      );

      if (handleRequestedPO) {
        this.openRequestedPurchaseOrder(allPOs, summarizedPOs);
      }
    } catch (error) {
      console.error('Failed to load purchase orders:', error);
      this.loadError = 'Unable to load purchase orders. Please try again.';
      this.message.error(this.loadError);
    } finally { this.loading = false; }
  }

  private openRequestedPurchaseOrder(allPOs: any[], summarizedPOs: any[]): void {
    if (this.SelectedPurchaseOrderSpread || !this.PurchaseOrderID) {
      return;
    }

    const targetId = Number(this.PurchaseOrderID);

    const matchingSpread = summarizedPOs.find((spread: any) =>
      this.spreadContainsPurchaseOrder(spread, targetId)
    );

    if (matchingSpread?.PurchaseOrderSpread) {
      this.openSpread(matchingSpread.PurchaseOrderSpread);
      return;
    }

    const purchaseOrder = allPOs.find(
      (po: any) => Number(po.PurchaseOrderID) === targetId
    );

    if (purchaseOrder) {
      this.openSinglePurchaseOrder(purchaseOrder);
      return;
    }

    window.alert(`Order ${this.PurchaseOrderID} Not Found.`);
    this.PurchaseOrderID = null;
  }

  private spreadContainsPurchaseOrder(spread: any, purchaseOrderId: number): boolean {
    const spreadIds = spread.PurchaseOrderSpread
      ?.split(/[,\s]+/)
      .map((value: string) => Number(value))
      .filter((value: number) => !Number.isNaN(value)) ?? [];

    const groupedIds = Array.isArray(spread.GroupedPurchaseOrders)
      ? spread.GroupedPurchaseOrders.map((po: any) => Number(po.PurchaseOrderID))
      : [];

    return spreadIds.includes(purchaseOrderId) || groupedIds.includes(purchaseOrderId);
  }

  private findPurchaseOrderById(purchaseOrderId: any): any | null {
    const targetId = Number(purchaseOrderId);

    for (const po of this.PurchaseOrders) {
      if (Number(po.PurchaseOrderID) === targetId && !po.PurchaseOrderSpread) {
        return po;
      }

      const groupedPO = po.GroupedPurchaseOrders?.find(
        (item: any) => Number(item.PurchaseOrderID) === targetId
      );

      if (groupedPO) {
        return groupedPO;
      }
    }

    return null;
  }

  private openSinglePurchaseOrder(purchaseOrder: any): void {
    this.resetView();
    this.selectedPurchaseOrder = cloneDraft(deepStrip(purchaseOrder, createEmptyPurchaseOrder()));
  }

  private openSpread(purchaseOrderSpread: string): void {
    this.resetView();
    this.SelectedPurchaseOrderSpread = purchaseOrderSpread;
  }

  private resetView(): void {
    this.selectedPurchaseOrder = null;
    this.selectedPurchaseOrders = undefined;
    this.SelectedPurchaseOrderSpread = undefined;
    this.MultiPurchaseOrder = false;
    this.PurchaseOrderID = null;
  }
}
